import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './ui/table';
import {
  Activity,
  Gauge,
  Target,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Feature 3 — Predictive HPA pre-scaling visibility + controls.
 *
 * Consumes the predictive-hpa management API (predictive_hpa_routes.py):
 *   GET  /api/clusters/{id}/predictive-hpa/status                  → this panel's data
 *   PUT  /api/clusters/{id}/predictive-hpa/mode                    → cluster opt-in mode
 *   PUT  /api/clusters/{id}/predictive-hpa/workloads/{ns}/{name}   → per-workload enable
 *
 * Renders three things the operator needs: the cluster mode toggle (always-on
 * vs opt-in-only), the recent predictive_scale/revert actions the engine emitted,
 * and the forecast-vs-actual hit/miss grades that say whether the predictions
 * were any good. Purely additive — owns its own fetch, mutates nothing else.
 */

const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
};

const fmtTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const errorPct = (forecast, actual) => {
  const f = Number(forecast);
  const a = Number(actual);
  if (!Number.isFinite(f) || f <= 0 || !Number.isFinite(a)) return null;
  return (Math.abs(a - f) / f) * 100;
};

function ActionBadge({ type }) {
  if (type === 'predictive_revert') {
    return (
      <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
        <ArrowDown className="w-3 h-3 mr-1" /> revert
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-blue-300 text-blue-700 dark:text-blue-400">
      <ArrowUp className="w-3 h-3 mr-1" /> pre-scale
    </Badge>
  );
}

function HitBadge({ hit }) {
  return hit ? (
    <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-400">
      <CheckCircle2 className="w-3 h-3 mr-1" /> hit
    </Badge>
  ) : (
    <Badge variant="outline" className="border-rose-300 text-rose-700 dark:text-rose-400">
      <XCircle className="w-3 h-3 mr-1" /> miss
    </Badge>
  );
}

/**
 * Tiny forecast-vs-actual sparkline for a workload row: the last N grades as
 * two overlaid lines (forecast = indigo #6366f1, actual = teal #0d9488 — the
 * pair passes the CVD/contrast checks on both light and dark surfaces), with
 * a dot on the last actual point colored by whether that grade was a hit.
 * Hand-rolled SVG (recharts inside a table cell fights the layout).
 */
function GradeSparkline({ grades }) {
  const pts = (grades || [])
    .map((g) => ({
      forecast: Number(g.forecast),
      actual: Number(g.actual),
      hit: !!g.hit,
    }))
    .filter((p) => Number.isFinite(p.forecast) && Number.isFinite(p.actual))
    .slice(-10);

  if (pts.length === 0) {
    return <span className="text-xs text-slate-400 dark:text-text-secondary">—</span>;
  }

  const w = 100;
  const h = 40;
  const pad = 4;
  const values = pts.flatMap((p) => [p.forecast, p.actual]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => (pts.length === 1 ? w / 2 : pad + (i * (w - 2 * pad)) / (pts.length - 1));
  const y = (v) => h - pad - ((v - min) / span) * (h - 2 * pad);
  const path = (key) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  const lastI = pts.length - 1;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Forecast (indigo) vs actual (teal) sparkline"
      className="overflow-visible"
    >
      <title>Forecast (indigo) vs actual (teal), last {pts.length} graded peaks</title>
      <path
        d={path('forecast')}
        fill="none"
        stroke="#6366f1"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d={path('actual')}
        fill="none"
        stroke="#0d9488"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* surface ring so the status dot separates from the lines it sits on */}
      <circle
        cx={x(lastI)}
        cy={y(last.actual)}
        r="4.5"
        fill="currentColor"
        className="text-white dark:text-zinc-900"
      />
      <circle
        cx={x(lastI)}
        cy={y(last.actual)}
        r="3"
        fill="currentColor"
        className={last.hit ? 'text-emerald-500' : 'text-rose-500'}
      />
    </svg>
  );
}

function AnnotationBadge({ tooltip }) {
  return (
    <Badge
      variant="outline"
      className="border-purple-300 text-purple-700 dark:text-purple-400 text-xs"
      title={tooltip}
    >
      annotation
    </Badge>
  );
}

function PredictiveHpaPanel({ clusterId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newNs, setNewNs] = useState('');
  const [newName, setNewName] = useState('');

  const fetchStatus = useCallback(async () => {
    if (!clusterId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/clusters/${clusterId}/predictive-hpa/status`);
      setStatus(res.data);
    } catch (error) {
      // The feature may simply have no activity yet — keep the panel quiet
      // rather than throwing a page-level error.
      console.log('Predictive HPA status not available:', error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const setMode = async (optInOnly) => {
    try {
      setSaving(true);
      await axios.put(`${API}/clusters/${clusterId}/predictive-hpa/mode`, {
        opt_in_only: optInOnly,
      });
      toast.success(
        optInOnly
          ? 'Predictive HPA set to opt-in only'
          : 'Predictive HPA set to always-on (all HPA workloads)'
      );
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mode');
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (isEnabled) => {
    try {
      setSaving(true);
      await axios.put(`${API}/clusters/${clusterId}/predictive-hpa/mode`, {
        enabled: isEnabled,
      });
      toast.success(
        isEnabled ? 'Predictive HPA enabled' : 'Predictive HPA disabled'
      );
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enabled state');
    } finally {
      setSaving(false);
    }
  };

  const setWorkload = async (namespace, workloadName, enabled) => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/clusters/${clusterId}/predictive-hpa/workloads/${encodeURIComponent(
          namespace
        )}/${encodeURIComponent(workloadName)}`,
        { enabled }
      );
      toast.success(`${namespace}/${workloadName} ${enabled ? 'enabled' : 'disabled'}`);
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workload');
    } finally {
      setSaving(false);
    }
  };

  const addWorkload = async () => {
    const ns = newNs.trim();
    const name = newName.trim();
    if (!ns || !name) {
      toast.error('Enter both namespace and workload name');
      return;
    }
    await setWorkload(ns, name, true);
    setNewNs('');
    setNewName('');
  };

  // Per-row toggle for the Workloads table: optimistic flip of
  // enabled_effective, then the same PUT, then a refetch (which also reverts
  // the optimistic state if the PUT failed).
  const setWorkloadRow = async (row, enabled) => {
    setStatus((prev) => {
      if (!prev || !Array.isArray(prev.workloads)) return prev;
      return {
        ...prev,
        workloads: prev.workloads.map((w) =>
          w.namespace === row.namespace && w.workload_name === row.workload_name
            ? { ...w, enabled_effective: enabled, enabled_source: 'setting' }
            : w
        ),
      };
    });
    try {
      setSaving(true);
      await axios.put(
        `${API}/clusters/${clusterId}/predictive-hpa/workloads/${encodeURIComponent(
          row.namespace
        )}/${encodeURIComponent(row.workload_name)}`,
        { enabled }
      );
      toast.success(`${row.namespace}/${row.workload_name} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workload');
    } finally {
      setSaving(false);
      await fetchStatus();
    }
  };

  if (!clusterId) return null;

  const optInOnly = !!status?.opt_in_only;
  // Master switch defaults on — absent field (older backend) means always-on.
  const enabled = status ? status.enabled !== false : true;
  // Universal predictive master switch (cluster-level). Carried on the status
  // payload; absent field (older backend) is treated as ON. When off, the panel
  // stays fully interactive (operators may pre-configure) but is visually
  // de-emphasized with a banner so the state is unmistakable.
  const masterEnabled = status?.master_enabled !== false;
  const enabledWorkloads = status?.enabled_workloads || [];
  const recentActions = status?.recent_actions || [];
  const recentGrades = status?.recent_grades || [];
  // Per-workload effective state (annotation-aware). Absent on older backends
  // — the section simply doesn't render then.
  const workloadRows = Array.isArray(status?.workloads) ? status.workloads : null;

  const gradedCount = recentGrades.length;
  const hitCount = recentGrades.filter((g) => g.hit).length;
  const hitRate = gradedCount > 0 ? Math.round((hitCount / gradedCount) * 100) : null;

  return (
    <Card data-testid="predictive-hpa-panel" className="border border-zinc-200 dark:border-zinc-800 shadow-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gauge className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              Predictive HPA Pre-Scaling
            </CardTitle>
            <CardDescription>
              Forecast-driven replica pre-scaling raises HPA <code>minReplicas</code> ahead of a
              predicted demand peak, then reverts after it passes.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatus}
            disabled={loading}
            data-testid="predictive-hpa-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Master-off banner — controls stay usable so operators can pre-configure. */}
        {!masterEnabled && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
            data-testid="predictive-hpa-master-off-banner"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Master switch is off — no predictive actions will be emitted. In-flight reverts still
              complete.
            </p>
          </div>
        )}

        <div className={`space-y-6 transition-opacity ${masterEnabled ? '' : 'opacity-60'}`}>
        {/* Summary row: enabled + mode + accuracy */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-surface-border">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Enabled</p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                {enabled
                  ? 'Predictive pre-scaling is active for this cluster.'
                  : 'Predictive pre-scaling is off — no actions are emitted.'}
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={saving}
              data-testid="predictive-hpa-enabled-toggle"
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-surface-border">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Opt-in only mode</p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                {optInOnly
                  ? 'Only workloads enabled below are pre-scaled.'
                  : 'Default: every HPA workload is eligible for pre-scaling.'}
              </p>
            </div>
            <Switch
              checked={optInOnly}
              onCheckedChange={setMode}
              disabled={saving}
              data-testid="predictive-hpa-mode-toggle"
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-surface-border">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                Forecast accuracy
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                {gradedCount > 0
                  ? `${hitCount}/${gradedCount} recent forecasts within tolerance`
                  : 'No graded forecasts yet'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                {hitRate === null ? '—' : `${hitRate}%`}
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary">hit rate</p>
            </div>
          </div>
        </div>

        {/* Per-workload effective state: toggle, accuracy, drift, next peak, sparkline */}
        {workloadRows && (
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              Workloads
            </p>
            {workloadRows.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-text-secondary">
                No HPA-managed workloads found in the latest metrics.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workload</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead className="text-right">Accuracy</TableHead>
                    <TableHead>Drift</TableHead>
                    <TableHead>Next predicted peak</TableHead>
                    <TableHead>Forecast vs actual</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workloadRows.map((w) => {
                    const annotationControlled =
                      w.enabled_source === 'annotation' || w.enabled_source === 'annotation_off';
                    const annotationTooltip =
                      'controlled by optops.io/predictive-hpa annotation';
                    return (
                      <TableRow key={`${w.namespace}/${w.workload_name}`}>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            {w.namespace}/{w.workload_name}
                          </p>
                          {w.hpa_name && (
                            <p className="text-xs text-slate-500 dark:text-text-secondary">
                              HPA: {w.hpa_name}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span title={annotationControlled ? annotationTooltip : undefined}>
                              <Switch
                                checked={!!w.enabled_effective}
                                onCheckedChange={(v) => setWorkloadRow(w, v)}
                                disabled={saving || annotationControlled}
                              />
                            </span>
                            {annotationControlled && (
                              <AnnotationBadge tooltip={annotationTooltip} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {w.accuracy_pct == null ? '—' : `${Math.round(w.accuracy_pct)}%`}
                        </TableCell>
                        <TableCell>
                          {w.drift_active ? (
                            <Badge
                              variant="outline"
                              className="border-amber-300 text-amber-700 dark:text-amber-400"
                            >
                              backing off
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400 dark:text-text-secondary">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600 dark:text-text-secondary whitespace-nowrap">
                          {fmtTime(w.next_predicted_peak)}
                        </TableCell>
                        <TableCell>
                          <GradeSparkline grades={w.grades} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {/* Opt-in workload management — only relevant in opt-in-only mode */}
        {optInOnly && (
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              Enabled workloads
            </p>
            {enabledWorkloads.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-text-secondary mb-3">
                No workloads enabled yet — add one below to start pre-scaling it.
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {enabledWorkloads.map((w) => (
                  <div
                    key={`${w.namespace}/${w.workload_name}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 dark:border-zinc-800"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {w.namespace}/{w.workload_name}
                      </p>
                      {w.updated_by && (
                        <p className="text-xs text-slate-500 dark:text-text-secondary">
                          updated by {w.updated_by}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={!!w.enabled}
                      onCheckedChange={(v) => setWorkload(w.namespace, w.workload_name, v)}
                      disabled={saving}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="namespace"
                value={newNs}
                onChange={(e) => setNewNs(e.target.value)}
                className="sm:w-40"
                data-testid="predictive-hpa-new-ns"
              />
              <Input
                placeholder="workload name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="sm:w-52"
                data-testid="predictive-hpa-new-name"
              />
              <Button onClick={addWorkload} disabled={saving} size="sm">
                Enable workload
              </Button>
            </div>
          </div>
        )}

        {/* Recent predictive actions */}
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            Recent predictive actions
          </p>
          {recentActions.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-text-secondary">
              No predictive scale/revert actions yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Workload</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentActions.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <ActionBadge type={a.action_type} />
                    </TableCell>
                    <TableCell className="font-medium">{a.current_instance || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{a.status || '—'}</Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-slate-600 dark:text-text-secondary">
                      {a.reason || '—'}
                    </TableCell>
                    <TableCell className="text-right text-slate-500 dark:text-text-secondary whitespace-nowrap">
                      {fmtTime(a.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Forecast vs actual grades */}
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            Forecast vs actual (graded peaks)
          </p>
          {recentGrades.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-text-secondary">
              No graded forecasts yet — grades appear after a predicted peak passes.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workload</TableHead>
                  <TableHead className="text-right">Forecast peak</TableHead>
                  <TableHead className="text-right">Actual peak</TableHead>
                  <TableHead className="text-right">Error</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="text-right">Graded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentGrades.map((g, i) => {
                  const err = errorPct(g.forecast_peak, g.actual_peak);
                  return (
                    <TableRow key={`${g.action_id || g.workload_name}-${i}`}>
                      <TableCell className="font-medium">
                        {g.namespace}/{g.workload_name}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtNum(g.forecast_peak)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtNum(g.actual_peak)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {err === null ? '—' : `${err.toFixed(0)}%`}
                      </TableCell>
                      <TableCell>
                        <HitBadge hit={!!g.hit} />
                      </TableCell>
                      <TableCell className="text-right text-slate-500 dark:text-text-secondary whitespace-nowrap">
                        {fmtTime(g.graded_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PredictiveHpaPanel;
